import { useState, useEffect } from "react";
import { useRef } from "react";
import { API } from "../../../api";

export default function UserEditor({ item, mode, onSave, onCancel, onPreviewUpdate, externalImage }) {
	const [formData, setFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		phone: "",
		image_source: "local",
		image_name: "",
		image_extension: "",
		imagefile: null,
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [urlError, setUrlError] = useState(null);
	const [urlLoadStatus, setUrlLoadStatus] = useState(null); // 'loading', 'success', 'error'
	const [livePreview, setLivePreview] = useState(true);
	const [previewDelay, setPreviewDelay] = useState(150);
	const [imageDirty, setImageDirty] = useState(false);
	const [originalImage, setOriginalImage] = useState({ image_source: null, image_name: null, image_extension: null });
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null); // Object URL créé localement via input fichier
    const [selectedFileName, setSelectedFileName] = useState("");
    const urlValidationRef = useRef();

	useEffect(() => {
		if (item) {
				const next = {
				first_name: item?.first_name ?? "",
				last_name: item?.last_name ?? "",
				email: item?.email ?? "",
				phone: item?.phone ?? "",
				image_source: item?.image_source ?? "local",
				image_name: item?.image_name ?? "",
				image_extension: item?.image_extension ?? "",
					imagefile: null,
				};
				setFormData(next);
				onPreviewUpdate(next);

				// Capture l'image d'origine pour permettre un retour arrière
				setOriginalImage({
					image_source: item.image_source || "local",
					image_name: item.image_name || "",
					image_extension: item.image_extension || "",
				});
				setImageDirty(false);
                setSelectedFileName("");
                setUrlError(null);
                setUrlLoadStatus(null);
		}
	}, [item]);

	// Synchronisation quand une image est déposée depuis l'aperçu (DnD)
	useEffect(() => {
		if (!externalImage) return;
		const { file, url, extension } = externalImage;
		setFormData((prev) => ({ ...prev, imagefile: file, image_extension: extension, image_source: "local" }));
		// On n'est pas propriétaire de cette URL (créée par le Manager), ne pas la révoquer ici
		onPreviewUpdate({ image_name: url, image_source: "preview", image_extension: extension });
		setImageDirty(true);
        setSelectedFileName(file?.name || "");
	}, [externalImage]);

	const debounceRef = useRef();

	// Nettoyage des timers à la destruction
	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			if (urlValidationRef.current) clearTimeout(urlValidationRef.current);
		};
	}, []);

	const schedulePreview = (partial, delay = previewDelay) => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			if (livePreview) onPreviewUpdate(partial);
		}, delay);
	};

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === "checkbox" ? checked : value;

		setFormData((prev) => ({ ...prev, [name]: newValue }));
		// Débounce pour champs texte/textarea, immédiat pour select/checkbox
		const isTextual = type === "text" || e.target.tagName === "TEXTAREA" || name === "first_name" || name === "last_name" || name === "email" || name === "phone";
		if (isTextual) schedulePreview({ [name]: newValue });
		else if (livePreview) onPreviewUpdate({ [name]: newValue });
	};

	const handleImageSourceChange = (e) => {
		const source = e.target.value;
		
		// Nettoyer un aperçu local éventuel
		if (localPreviewUrl) {
			try { URL.revokeObjectURL(localPreviewUrl); } catch {}
			setLocalPreviewUrl(null);
		}
        setSelectedFileName("");
        setUrlError(null);
        setUrlLoadStatus(null);
		
		// Préserver l'image d'origine dans l'aperçu jusqu'à ce qu'une nouvelle soit fournie
		if (source === "web" && originalImage.image_source?.toLowerCase() === "web") {
			// Restaurer l'URL d'origine si on revient au type web
			setFormData((prev) => ({ ...prev, image_source: source, image_name: originalImage.image_name || "", imagefile: null }));
			onPreviewUpdate({ image_source: "web", image_name: originalImage.image_name || "" });
		} else if (source === "local" && originalImage.image_source?.toLowerCase() === "local") {
			// Restaurer l'image locale d'origine si on revient au type local
			setFormData((prev) => ({ ...prev, image_source: source, image_name: originalImage.image_name || "", image_extension: originalImage.image_extension || "", imagefile: null }));
			onPreviewUpdate({ image_source: "local", image_name: originalImage.image_name || "", image_extension: originalImage.image_extension || "" });
		} else {
			// Changer de type: vider le formulaire mais garder l'aperçu de l'original
			setFormData((prev) => ({ ...prev, image_source: source, image_name: "", image_extension: "", imagefile: null }));
			// Garder l'aperçu de l'original visible
			if (originalImage.image_name) {
				onPreviewUpdate({ 
					image_source: originalImage.image_source || "local", 
					image_name: originalImage.image_name,
					image_extension: originalImage.image_extension || "",
					urlLoadError: false
				});
			}
		}
		setImageDirty(false);
	};

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/avif"];
		if (!validTypes.includes(file.type)) {
			setError("Format de fichier non supporté (png, jpg, jpeg, gif, webp, avif uniquement)");
			return;
		}

		const ext = file.name.split(".").pop().toLowerCase();
		setFormData((prev) => ({ ...prev, imagefile: file, image_extension: ext, image_source: "local" }));

		// Nettoyer l'ancienne Object URL si elle existe
		if (localPreviewUrl) {
			try { URL.revokeObjectURL(localPreviewUrl); } catch {}
		}

		// Créer un aperçu local via Object URL
		const url = URL.createObjectURL(file);
		setLocalPreviewUrl(url);
		// Toujours afficher l'aperçu pour les images (même si livePreview est désactivé)
		onPreviewUpdate({ image_name: url, image_source: "preview", image_extension: ext });

		setError(null);
		setImageDirty(true);
        setSelectedFileName(file.name);
	};

	const handleUrlChange = (e) => {
		const url = e.target.value.trim();
		setFormData((prev) => ({ ...prev, image_name: url }));
		
		// URL vide: revenir à l'image d'origine si on est en mode édition
		if (!url) {
			setUrlError(null);
			setUrlLoadStatus(null);
			setImageDirty(false);
			
			if (mode === "edit" && originalImage.image_name) {
				// Garder l'image d'origine visible
				onPreviewUpdate({ 
					image_source: originalImage.image_source || "local", 
					image_name: originalImage.image_name,
					image_extension: originalImage.image_extension || "",
					urlLoadError: false
				});
			} else {
				// Mode création: placeholder
				onPreviewUpdate({ image_name: "", image_source: "web", urlLoadError: false });
			}
			return;
		}
		
		// Marquer comme modifié dès qu'on tape quelque chose
		 setImageDirty(true);
		setUrlLoadStatus('loading');
		
		// Nettoyer le timer précédent
		if (urlValidationRef.current) clearTimeout(urlValidationRef.current);
		
		// Validation en arrière-plan
		urlValidationRef.current = setTimeout(() => {
			// Validation de l'URL
			try {
				const urlObj = new URL(url);
				
				if (!['http:', 'https:'].includes(urlObj.protocol)) {
					setUrlError("L'URL doit commencer par http:// ou https://");
					setUrlLoadStatus('error');
					onPreviewUpdate({ image_name: url, image_source: "web", urlLoadError: true });
					return;
				}
				
				// Vérifier l'extension dans l'URL
				const pathname = urlObj.pathname.toLowerCase();
				const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif'];
				const hasValidExt = validExtensions.some(ext => pathname.endsWith(ext));
				
				if (!hasValidExt && pathname !== '/' && !pathname.includes('?')) {
					setUrlError("L'URL ne semble pas pointer vers une image (jpg, png, gif, webp, avif)");
				}
				
				// Tester le chargement réel de l'image
				const img = new Image();
				
				// Important: définir les handlers AVANT de set le src
				img.onload = () => {
					setUrlError(null);
					setUrlLoadStatus('success');
					// Toujours mettre à jour le preview sur succès
					onPreviewUpdate({ image_name: url, image_source: "web", urlLoadError: false });
				};
				
				img.onerror = () => {
					setUrlError("Impossible de charger l'image depuis cette URL");
					setUrlLoadStatus('error');
					onPreviewUpdate({ image_name: url, image_source: "web", urlLoadError: true });
				};
				
				// Déclencher le chargement
				img.src = url;
				
			} catch (err) {
				// URL pas encore complète pendant la saisie
				setUrlError(null);
				setUrlLoadStatus('loading');
				// Ne pas mettre urlLoadError à true si c'est juste une URL incomplète
				onPreviewUpdate({ image_name: url, image_source: "web", urlLoadError: false });
			}
		}, 400);
	};

	// Quand on réactive l'aperçu en direct, on resynchronise l'état complet
	useEffect(() => {
		if (!livePreview) return;
		// Ne pas écraser l'aperçu temporaire quand un fichier local est sélectionné
		if (formData.image_source === "local" && formData.imagefile) return;
		onPreviewUpdate(formData);
	}, [livePreview, formData, onPreviewUpdate]);

	const handleRevertImage = () => {
		if (!originalImage || (!originalImage.image_name && !originalImage.image_source)) return;
		
		const origType = (originalImage.image_source || "local").toLowerCase();
		
		setFormData((prev) => ({
			...prev,
			image_source: originalImage.image_source || "local",
			image_name: originalImage.image_name || "",
			image_extension: originalImage.image_extension || "",
			imagefile: null,
		}));
		
		// Nettoyer l'aperçu local si présent
		if (localPreviewUrl) {
			try { URL.revokeObjectURL(localPreviewUrl); } catch {}
			setLocalPreviewUrl(null);
		}
		
		// Toujours mettre à jour l'aperçu avec l'image d'origine
		if (origType === "web") {
			onPreviewUpdate({ image_name: originalImage.image_name, image_source: "web", urlLoadError: false });
		} else {
			onPreviewUpdate({ image_name: originalImage.image_name, image_source: origType, image_extension: originalImage.image_extension, urlLoadError: false });
		}
		
		setImageDirty(false);
        setSelectedFileName("");
        setUrlError(null);
        setUrlLoadStatus(null);
	}; 

    // Nettoyage à la destruction du composant
    useEffect(() => {
        return () => {
            if (localPreviewUrl) {
                try { URL.revokeObjectURL(localPreviewUrl); } catch {}
            }
        };
    }, [localPreviewUrl]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError(null);

		try {
			await onSave({
				...(mode === "edit" && item?.id ? { id: item.id } : {}),
				first_name: formData.first_name,
				last_name: formData.last_name,
				email: formData.email,
				phone: formData.phone,
				image_source: formData.image_source,
				image_name: formData.image_name,
				image_extension: formData.image_extension,
				oldFileName: mode === "edit" ? item?.image_name + '.' + item?.image_extension : null,
			}, formData?.imagefile || null);
		} catch (err) {
			setError(err.message || "Erreur lors de la sauvegarde");
			setSaving(false);
		}
	};

	return (
		<div className="carrousel-editor">
			<div className="editor-header">
				<h3>{mode === "create" ? "Créer un profil utilisateur" : "Modifier le profil utilisateur"}</h3>
				<div className="editor-tools">
					<label className="tool-inline">
						<input type="checkbox" checked={livePreview} onChange={(e) => setLivePreview(e.target.checked)} />
						<span>Aperçu en direct</span>
					</label>
					<label className="tool-inline">
						<span>Délai</span>
						<select value={previewDelay} onChange={(e) => setPreviewDelay(Number(e.target.value))}>
							<option value={0}>0 ms</option>
							<option value={100}>100 ms</option>
							<option value={150}>150 ms</option>
							<option value={250}>250 ms</option>
						</select>
					</label>
				</div>
			</div>

			<form onSubmit={handleSubmit} className="editor-form">
				<label className="form-field">
					<span>Prénom</span>
					<input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Ex: Jean" required />
				</label>

				<label className="form-field">
					<span>Nom</span>
					<input type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Ex: Dupont" required />
				</label>

				<label className="form-field">
					<span>Email</span>
					<input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="exemple@email.com" required />
				</label>

				<label className="form-field">
					<span>Téléphone</span>
					<input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="06 12 34 56 78" />
				</label>

				<div className="form-field">
					<span>Source de l'image</span>
					<div className="radio-group">
						<label><input type="radio" value="local" checked={formData.image_source === "local"} onChange={handleImageSourceChange} />Fichier local</label>
						<label><input type="radio" value="web" checked={formData.image_source === "web"} onChange={handleImageSourceChange} />URL externe</label>
					</div>
				</div>

				{formData.image_source === "local" ? ( 
					<label className="form-field">
						<span>Sélectionner une image</span>
						<input type="file" accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/avif" onChange={handleFileChange} />
						{selectedFileName && (
							<div className="file-selected-name">Fichier sélectionné : {selectedFileName}</div>
						)}
						<small>Formats acceptés : PNG, JPG, JPEG, GIF, WEBP, AVIF</small>
						{mode === "edit" && (
							<div>
								<button type="button" className="btn small" onClick={handleRevertImage} disabled={!imageDirty} title="Revenir à l'image d'origine">
									Revenir à l'image d'origine
								</button>
							</div>
						)}
					</label>
				) : (
					<label className="form-field">
						<span>URL de l'image</span>
						<input type="url" value={formData.image_name} onChange={handleUrlChange} placeholder="https://..." required />
						{urlError && (
							<small className="error-message" style={{ color: '#e74c3c', marginTop: '4px', display: 'block' }}>
								⚠️ {urlError}
							</small>
						)}
						{mode === "edit" && (
							<div>
								<button type="button" className="btn small" onClick={handleRevertImage} disabled={!imageDirty} title="Revenir à l'image d'origine">
									Revenir à l'image d'origine
								</button>
							</div>
						)}
					</label>
				)}

				{error && <div className="form-error">{error}</div>}

				<div className="form-actions">
					<button type="button" className="btn ghost" onClick={onCancel} disabled={saving}>Annuler</button>
					<button type="submit" className="btn" disabled={saving}>{saving ? "Enregistrement..." : mode === "create" ? "Créer" : "Enregistrer"}</button>
				</div>
			</form>
		</div>
	);
}
